import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { authApi } from "@/lib/api";
import { Spinner } from "@/components/ui/spinner";
import {
  Lock,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  Eye,
  EyeOff,
} from "lucide-react";
import logoSvg from "@/assets/logo.svg";

const resetPasswordSchema = z
  .object({
    password: z
      .string()
      .min(10, "Lösenordet måste vara minst 10 tecken")
      .regex(/[A-Z]/, "Lösenordet måste innehålla minst en versal (A-Z)")
      .regex(/[a-z]/, "Lösenordet måste innehålla minst en gemen (a-z)")
      .regex(/[0-9]/, "Lösenordet måste innehålla minst en siffra (0-9)")
      .regex(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/, "Lösenordet måste innehålla minst ett specialtecken"),
    confirmPassword: z.string().min(1, "Bekräfta ditt lösenord"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Lösenorden matchar inte",
    path: ["confirmPassword"],
  });

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const password = form.watch("password") || "";
  const passwordRequirements = {
    minLength: password.length >= 10,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
  };
  const isPasswordValid = Object.values(passwordRequirements).every(Boolean);

  const handleSubmit = async (data: ResetPasswordFormData) => {
    if (!token) return;
    if (!isPasswordValid) {
      setError("Lösenordet uppfyller inte alla krav.");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await authApi.resetPassword(token, data.password);

      if (result.success) {
        setSuccess(true);
        toast.success("Lösenordet har ändrats");
      } else {
        setError(result.error?.message || "Kunde inte återställa lösenordet. Försök igen.");
        toast.error("Kunde inte återställa lösenordet");
      }
    } catch {
      setError("Ett oväntat fel uppstod. Försök igen.");
      toast.error("Ett oväntat fel uppstod");
    }

    setIsLoading(false);
  };

  // Ingen token i URL
  if (!token) {
    return (
      <div className="min-h-screen bg-gray-50 text-gray-900 flex items-center justify-center px-4" >
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <Link to="/">
              <img src={logoSvg} alt="Kvanten" className="h-10 w-auto mx-auto" />
            </Link>
          </div>
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="text-red-600" size={32} />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Ogiltig länk</h2>
            <p className="text-gray-600 mb-6">
              Återställningslänken saknar en giltig token. Begär en ny återställning.
            </p>
            <Link
              to="/"
              className="btn btn-primary inline-flex items-center space-x-2"
            >
              <ArrowLeft size={20} />
              <span>Till startsidan</span>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 flex items-center justify-center px-4" >
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/">
            <img src={logoSvg} alt="Kvanten" className="h-10 w-auto mx-auto" />
          </Link>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8">
          {success ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="text-green-600" size={32} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Lösenordet har ändrats
              </h2>
              <p className="text-gray-600 mb-6">
                Ditt lösenord har uppdaterats. Du kan nu logga in med ditt nya lösenord.
              </p>
              <Link
                to="/"
                className="btn btn-primary w-full inline-flex items-center justify-center space-x-2"
              >
                <ArrowLeft size={20} />
                <span>Gå till inloggning</span>
              </Link>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  Välj nytt lösenord
                </h2>
                <p className="text-gray-600 mt-1">
                  Ange ditt nya lösenord nedan
                </p>
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
                  <AlertCircle className="text-red-600 mt-0.5 flex-shrink-0" size={20} />
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nytt lösenord
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type={showPassword ? "text" : "password"}
                      {...form.register("password")}
                      className="input pl-10 pr-10"
                      placeholder="Minst 10 tecken"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>

                  {password.length > 0 && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600 font-medium mb-2">
                        Lösenordet måste innehålla:
                      </p>
                      <ul className="text-xs space-y-1">
                        <li className={`flex items-center space-x-2 ${passwordRequirements.minLength ? "text-green-600" : "text-gray-400"}`}>
                          <span>{passwordRequirements.minLength ? "\u2713" : "\u25CB"}</span>
                          <span>Minst 10 tecken</span>
                        </li>
                        <li className={`flex items-center space-x-2 ${passwordRequirements.hasUppercase ? "text-green-600" : "text-gray-400"}`}>
                          <span>{passwordRequirements.hasUppercase ? "\u2713" : "\u25CB"}</span>
                          <span>Minst en versal (A-Z)</span>
                        </li>
                        <li className={`flex items-center space-x-2 ${passwordRequirements.hasLowercase ? "text-green-600" : "text-gray-400"}`}>
                          <span>{passwordRequirements.hasLowercase ? "\u2713" : "\u25CB"}</span>
                          <span>Minst en gemen (a-z)</span>
                        </li>
                        <li className={`flex items-center space-x-2 ${passwordRequirements.hasNumber ? "text-green-600" : "text-gray-400"}`}>
                          <span>{passwordRequirements.hasNumber ? "\u2713" : "\u25CB"}</span>
                          <span>Minst en siffra (0-9)</span>
                        </li>
                        <li className={`flex items-center space-x-2 ${passwordRequirements.hasSpecial ? "text-green-600" : "text-gray-400"}`}>
                          <span>{passwordRequirements.hasSpecial ? "\u2713" : "\u25CB"}</span>
                          <span>Minst ett specialtecken (!@#$%...)</span>
                        </li>
                      </ul>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bekräfta lösenord
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type={showPassword ? "text" : "password"}
                      {...form.register("confirmPassword")}
                      className="input pl-10"
                      placeholder="Upprepa lösenordet"
                    />
                  </div>
                  {form.formState.errors.confirmPassword && (
                    <p className="text-sm text-red-600 mt-1">
                      {form.formState.errors.confirmPassword.message}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading || !isPasswordValid}
                  className="w-full btn btn-primary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? <Spinner size="sm" /> : <span>Spara nytt lösenord</span>}
                </button>
              </form>

              <div className="mt-6 text-center">
                <Link
                  to="/"
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium inline-flex items-center space-x-1"
                >
                  <ArrowLeft size={16} />
                  <span>Tillbaka till inloggning</span>
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
